import { PayloadAction } from '@reduxjs/toolkit';
import { put, takeLatest } from 'redux-saga/effects';
import { ProjectId, UserId } from 'api/models';
import { ProjectAPI } from 'api/project';
import { toastService } from 'components/molecules/toast';
import { InvitedMembers, addUsers } from 'data/members/slice';
import {
  CreateAndInviteProjectMembers,
  createAndInviteProjectMembersAction,
} from 'data/projects/actions';
import { getProjectMembersQueryKey, queryClient } from 'queries';
import {
  MEMBERS_V2_MODIFIED_QUERY_KEY,
  MEMBERS_V2_QUERY_KEY,
} from 'queries/members';
import { add } from 'stores/memberships';
import { addedAll } from 'stores/users';

interface Callback {
  (data: any): void;
}

function* createAndInviteProjectMembers(
  action: PayloadAction<{
    projectId: number;
    data: (CreateAndInviteProjectMembers | InvitedMembers)[];
    handleSuccess?: Callback;
    handleFailure?: Callback;
  }>,
) {
  const { payload } = action;
  const { data, projectId, handleSuccess, handleFailure } = payload;

  try {
    const newUsers = (data as CreateAndInviteProjectMembers[]).filter(
      ({ user }) => user.emailId != null,
    ) as CreateAndInviteProjectMembers[];
    const existingUsers = (data as InvitedMembers[]).filter(
      ({ user }) => user.userId != null,
    ) as InvitedMembers[];

    const createdAndInvitedResponse =
      newUsers.length > 0
        ? yield* (function* createAndInviteNew() {
            try {
              return yield ProjectAPI.createAndInviteProjectMembers(
                projectId,
                newUsers,
              );
            } catch (error: any) {
              const errorData = error?.response?.data?.errors?.[0];
              const errorCode = errorData?.code;
              const errorReason = errorData?.reason;

              // Handle specific error codes with better messages
              // Backend error reason takes precedence, fallback to default messages
              let errorTitle = 'Failed to add team members';
              let errorMessage =
                errorReason || 'Could not add team members to your project';

              switch (errorCode) {
                case 'INVALID_INPUTS':
                  errorTitle = 'Invalid Input';
                  errorMessage =
                    errorReason ||
                    'One or more email addresses are invalid or already exist in the account.';
                  break;
                case 'DUPLICATE_USER':
                  errorTitle = 'Already a Member';
                  errorMessage =
                    errorReason ||
                    'One or more users are already members of this project.';
                  break;
                case 'PERMISSION_DENIED':
                  errorTitle = 'Permission Denied';
                  errorMessage =
                    errorReason ||
                    'You do not have permission to invite these users.';
                  break;
                case 'COMPANY_CONFLICT':
                  errorTitle = 'User Conflict';
                  errorMessage =
                    errorReason ||
                    'One or more users already exist in a different company.';
                  break;
                default:
                  // Use backend reason as-is for unknown codes
                  errorMessage = errorReason || errorMessage;
              }

              toastService({
                title: errorTitle,
                message: errorMessage,
              }).error();

              // Pass error to handleFailure for additional handling
              if (handleFailure) {
                handleFailure(error);
              }

              return [];
            }
          })()
        : [];
    const invitedResponse =
      existingUsers.length > 0
        ? yield ProjectAPI.addMembers(projectId, existingUsers)
        : [];

    if (handleSuccess) {
      handleSuccess(payload);
    }

    yield put(addUsers(createdAndInvitedResponse.map(({ user }) => user)));
    yield put(addedAll(createdAndInvitedResponse.map(({ user }) => user)));

    yield put(
      add({
        projectId: ProjectId(projectId),
        memberships: createdAndInvitedResponse
          .concat(invitedResponse)
          .map(
            ({
              user: { userId },
              permissionSet: { permissionSetType: memberType },
              joinedAt,
            }) => ({ userId: UserId(userId), memberType, joinedAt }),
          ),
      }),
    );
    yield queryClient.refetchQueries(
      getProjectMembersQueryKey(ProjectId(projectId)),
    );
    yield queryClient.refetchQueries(MEMBERS_V2_QUERY_KEY);
    yield queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
    // Refetch budget exclusions queries for the project
    yield queryClient.refetchQueries([
      'project-members-exclusion',
      ProjectId(projectId),
    ]);
    yield queryClient.refetchQueries(['budget-member-exclusion']);
  } catch (error) {
    if (handleFailure) {
      handleFailure(error);
    }
  }
}

export const watchCreateAndInviteProjectMembers = function* (): Generator {
  yield takeLatest(
    createAndInviteProjectMembersAction,
    createAndInviteProjectMembers,
  );
};
